import {
  buildOrderLiteral,
  buildPagingLiteral,
  buildWhereLiteral,
  OrderBy,
} from '@/lib/graphql-query-builder';

/**
 * The Data Gateway's `get<Schema>s` queries take native `where` / `order` / `paging` arguments
 * instead of a single JSON-string `input: DynamicQueryInput` — see graphql-query-builder.ts for
 * why these are inlined as literals rather than passed as typed variables. Paged lists sort on the
 * server (`buildOrderLiteral`), so each page is a slice of the sorted whole.
 */
/**
 * Newest request first by default, like the old my-TOIL list's
 * `OrderByDescending(x => x.CreateDate)`.
 */
export const DEFAULT_TOIL_REQUESTS_ORDER: OrderBy[] = [{ field: 'CreatedDate', direction: 'DESC' }];

export const buildGetToilRequestsQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number,
  order: OrderBy[] = DEFAULT_TOIL_REQUESTS_ORDER
) => `
  query {
    ToilRequests: getToilRequests(
      where: ${buildWhereLiteral(where)}
      order: ${buildOrderLiteral(order)}
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      hasNextPage
      hasPreviousPage
      totalCount
      totalPages
      pageSize
      pageNo
      items {
        ItemId
        UserId
        DisplayName
        Email
        DepartmentId
        DepartmentName
        DepartmentKey
        ToilDate
        ToilType
        Duration
        Reason
        Status
        IsArchived
        CreatedDate
        Note
        ToilCategory
      }
    }
  }
`;

/** Category and length of every TOIL request that counts towards one term's earned summary. */
export const buildGetToilSummaryQuery = (where: Record<string, unknown>, pageSize: number) => `
  query {
    ToilRequests: getToilRequests(
      where: ${buildWhereLiteral(where)}
      order: []
      paging: ${buildPagingLiteral(1, pageSize)}
    ) {
      items {
        ToilCategory
        Duration
      }
    }
  }
`;

/**
 * The working days of every HR term of the requester (the caller picks the term covering the day,
 * `hrTermOn`): `WorkDays` is a list
 * embedded on `UserHRPeriodicalProfile` (`[HRWorkDay]` — the separate `UserHRWorkDay` collection no
 * longer exists). Used to tell their "weekend" (days not worked) from a regular working day, like
 * the legacy .NET `AttendanceManagementValidationExtensionService.IsWeekend`.
 *
 * Known gap: `UserHRPeriodicalProfile.UserId` is populated with the `UserProfileDetail` document's
 * own id by `createEmployee` in hr-directory for terms created before that was fixed, not the IAM
 * user id. Filtering by the auth `userId` will find no row for those older terms, and callers fall
 * back to their default work days for them.
 */
export const buildGetHrWorkDaysQuery = (userId: string) => `
  query {
    HrProfiles: getUserHRPeriodicalProfiles(
      where: ${buildWhereLiteral({ UserId: userId })}
      order: []
      paging: ${buildPagingLiteral(1, 50)}
    ) {
      items {
        HRPeriodStartDate
        HRPeriodEndDate
        IsCurrentHrYear
        WorkDays {
          WorkDay
          WorkingHours
          EffectiveFrom
        }
      }
      totalCount
    }
  }
`;

/**
 * Holidays on a single local day in the active organization (every query is scoped to it, see
 * `buildWhereLiteral`) — used to tell a public-holiday TOIL date from a regular working day.
 * Canton-level scoping (a holiday list can be further narrowed to specific Cantons via
 * `IsRegional`/`CantonIds`) isn't checked here since the requester's own Canton isn't available
 * in this flow.
 *
 * A `Holiday` is one day, stored date only in `StartDate` (the day at 00:00:00 UTC), so `dayRange` is
 * `{ $gte: day 00:00Z, $lt: next day 00:00Z }` (`dateOnlyRangeClause`). The bounds must be full
 * ISO-8601 values: a bare "YYYY-MM-DD" literal fails `where`-filter parsing ("DateTime cannot parse
 * the given literal of type StringValueNode").
 */
export const buildGetHolidaysOnDateQuery = (dayRange: { $gte?: string; $lt?: string }) => `
  query {
    Holidays: getHolidays(
      where: ${buildWhereLiteral({ StartDate: dayRange })}
      order: []
      paging: ${buildPagingLiteral(1, 1)}
    ) {
      totalCount
    }
  }
`;
